import { IRawStyle } from "@fluentui/react";
import React, { PropsWithChildren, useEffect, useState } from "react";

import { SprintAccessRight } from "../../../../../core/security/SprintAccessRight";
import { NoteSubjectType } from "../../../../types/NoteSubjectType";
import { useNotesActions } from "../useNotesActions";
import { NoteItem } from "./NoteItem";
import { NotesStateContext } from "./NotesStateContext";

type NotesStateProviderProps = PropsWithChildren<{
    subjectId: string;
    sourceIds: string[];
    subjectType: NoteSubjectType;
    isDeleteSupported: boolean;
    isHeader: boolean;
    isInlineCreateSupported: boolean;
    style?: IRawStyle;
    defaultNotes?: NoteItem[];
    accessRights: SprintAccessRight[];
    disabled?: boolean;
    renderBadge?: (note: NoteItem) => JSX.Element;
}>;

export const NotesStateProvider: React.FC<NotesStateProviderProps> = ({
    subjectId,
    sourceIds,
    subjectType,
    isDeleteSupported,
    isInlineCreateSupported,
    isHeader,
    style,
    children,
    defaultNotes,
    accessRights,
    disabled,
    renderBadge
}: NotesStateProviderProps) => {
    const { getNotes } = useNotesActions();

    const [notes, setNotes] = useState<NoteItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (defaultNotes?.length) {
            setNotes(defaultNotes);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        getNotes(sourceIds).then((response) => {
            setNotes(response);
            setIsLoading(false);
        });
    }, [sourceIds, defaultNotes]);

    return (
        <NotesStateContext.Provider
            value={{
                notes,
                setNotes,
                isLoading,
                subjectId,
                sourceIds,
                subjectType,
                isInlineCreateSupported,
                isHeader,
                isDeleteSupported,
                style,
                accessRights,
                disabled,
                renderBadge
            }}
        >
            {children}
        </NotesStateContext.Provider>
    );
};
